import { LoadingSpinner } from "@/app/loading.tsx"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog.tsx"
import { toast } from "@/components/ui/use-toast.ts"
import { classes } from "@/utils"
import { capitalCase } from "change-case"
import { format } from "date-fns"
import { Download, FileSearch, Loader2, Trash2, Upload } from "lucide-react"
import { HTMLAttributes, useMemo } from "preact/compat"
import { useState } from "preact/hooks"
import { useDropzone } from "react-dropzone"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { GoogleDriveSearch } from "./GoogleDriveSearch.tsx"
import { BolRequestButtons } from "./BolRequestButtons.tsx"
import { DeductionDetailJson, DeductionExt, parseFile, ParseFileMode } from "@/api"
import { set_backup_from_user_file } from "@/api/accounting_wizard"
import { Button } from "@/components/ui/button"

export interface DeductionFile {
  display_name: string
  date?: string
  s3_uri: string
  deduction_id?: string
  actor_name?: string
}

export function handleViewS3Uri(s3_uri: string) {
  return function handleClick(e: MouseEvent) {
    e.preventDefault()
    e.stopImmediatePropagation()
    window.open(`/api/_s3?s3_uri=${encodeURIComponent(s3_uri)}`, "_blank")
  }
}

async function uploadFiles(deductionId: string, files: File[]): Promise<DeductionFile[]> {
  const formData = new FormData()
  for (const file of files) {
    formData.append("files", file)
  }

  const response = await fetch(`/api/deduction/${deductionId}/files`, {
    method: "POST",
    credentials: "same-origin",
    body: formData,
  })

  if (!response.ok) {
    throw new Error("Failed to upload files")
  }

  return response.json()
}

async function handleFileDelete(deductionId: string, s3Uri: string): Promise<DeductionFile[]> {
  const encodedUri = encodeURIComponent(s3Uri)
  const response = await fetch(`/api/deduction/${deductionId}/files/${encodedUri}`, {
    method: "DELETE",
    credentials: "same-origin",
  })

  if (!response.ok) {
    throw new Error("Failed to delete file")
  }

  return response.json()
}

interface DeleteFileDialogProps {
  file: DeductionFile
  deductionId: string
  onDelete: () => void
  className?: string
}

function DeleteFileDialog({ file, deductionId, onDelete, className }: DeleteFileDialogProps) {
  async function handleConfirmDelete() {
    try {
      await handleFileDelete(deductionId, file.s3_uri)
      toast({
        title: "Success",
        description: "File deleted successfully",
      })
      onDelete()
    } catch (error) {
      console.error("Error deleting file:", error)
      toast({
        title: "Error",
        description: "Failed to delete file",
        variant: "destructive",
      })
    }
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          onClick={e => e.stopPropagation()}
          class={classes("p-1.5 rounded-full transition-colors cursor-pointer", className)}>
          <Trash2 className="h-4 w-4 text-red-500 hover:text-red-600" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete the file.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={(e: Event) => e.stopPropagation()}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e: Event) => {
              e.stopPropagation()
              handleConfirmDelete()
            }}>
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

interface SetBackupDialogProps {
  file: DeductionFile
  distributor_label: string
  setting_backup_uri: string | null
  onConfirm: (s3_uri: string) => void
}

function SetBackupDialog({
  file,
  distributor_label,
  setting_backup_uri,
  onConfirm,
}: SetBackupDialogProps) {
  const busy = setting_backup_uri === file.s3_uri

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          class="text-xs h-8 px-2 shrink-0 inline-flex items-center gap-1.5"
          disabled={busy}
          onClick={(e: Event) => e.stopPropagation()}>
          {busy ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
              <span>Saving…</span>
            </>
          ) : (
            "Use as official backup"
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent onClick={(e: Event) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Use this file as the official backup?</AlertDialogTitle>
          <AlertDialogDescription>
            You can attach as many files as you like — this only changes which one counts as the
            official backup. This deduction already has an official backup from {distributor_label};
            making this file official means it is the document used for coding, CodingWiz parsing
            and disputes instead. The {distributor_label} file stays attached here, it is just no
            longer the official one.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={(e: Event) => e.stopPropagation()}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e: Event) => {
              e.stopPropagation()
              onConfirm(file.s3_uri)
            }}>
            Use as official backup
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

interface FilesProps {
  deduction: DeductionDetailJson
  onParseComplete?: (fileKind: string) => void
}

export async function handleFileUpload(deductionId: string, files: File[]): Promise<DeductionFile[]> {
  try {
    const newFiles = await uploadFiles(deductionId, files)
    RefreshFlag.set(flag => !flag)
    return newFiles.map((file: DeductionFile) => ({ ...file, deduction_id: deductionId }))
  } catch (error) {
    console.error("Error uploading files:", error)
    return []
  }
}

/**
 * Files the user just uploaded, shown ahead of the server-derived list so a drag-and-drop appears
 * immediately instead of only after a page refresh. De-duplicated by `s3_uri`, so each entry
 * collapses into the server's own row as soon as the refetched deduction carries it.
 */
export function files_with_pending_uploads(
  files: DeductionFile[],
  pending: DeductionFile[]
): DeductionFile[] {
  if (pending.length === 0) {
    return files
  }
  const known = new Set(files.map((file: DeductionFile) => file.s3_uri))
  return [...pending.filter((file: DeductionFile) => !known.has(file.s3_uri)), ...files]
}

/**
 * The file list to render: the deduction's own files, plus uploads the refetch has not caught up
 * with, minus ones deleted in this session.
 *
 * `deleted` is what makes a delete leave the list at once rather than on the next page load, and
 * it also has to outrank `pending` — otherwise deleting a file uploaded moments earlier would let
 * the optimistic upload row resurrect it.
 */
export function visible_deduction_files(
  files: DeductionFile[],
  pending: DeductionFile[],
  deleted: Set<string>
): DeductionFile[] {
  const merged = files_with_pending_uploads(files, pending)
  if (deleted.size === 0) {
    return merged
  }
  return merged.filter((file: DeductionFile) => !deleted.has(file.s3_uri))
}

/** Matches server `db::s3::is_user_uploaded_file_uri`. */
export function s3_uri_is_user_uploaded_file(uri: string): boolean {
  if (!uri.startsWith("s3://")) {
    return false
  }
  const rest = uri.slice("s3://".length)
  const firstSlash = rest.indexOf("/")
  if (firstSlash === -1) {
    return false
  }
  const key = rest.slice(firstSlash + 1)
  return key.includes("/user_files/deduction/")
}

/** Matches server `db::s3::is_distributor_managed_backup_uri`. */
export function s3_uri_is_distributor_managed_backup(uri: string): boolean {
  const trimmed = uri.trim()
  return trimmed.length > 0 && !s3_uri_is_user_uploaded_file(trimmed)
}

export function deduction_has_distributor_managed_backup(deduction: DeductionExt): boolean {
  if (!deduction.backup_s3_uri) {
    return false
  }
  return s3_uri_is_distributor_managed_backup(deduction.backup_s3_uri)
}

export function set_as_backup_requires_confirm(deduction: DeductionExt): boolean {
  return deduction_has_distributor_managed_backup(deduction)
}

function can_set_as_official_backup(file: DeductionFile, deduction: DeductionExt): boolean {
  if (!file.deduction_id) {
    return false
  }
  if (file.s3_uri === deduction.backup_s3_uri) {
    return false
  }
  if (file.s3_uri === deduction.check_s3_uri) {
    return false
  }
  return true
}

function can_delete_user_file(file: DeductionFile, deduction: DeductionExt): boolean {
  if (!file.deduction_id) {
    return false
  }
  if (
    file.s3_uri === deduction.backup_s3_uri &&
    s3_uri_is_distributor_managed_backup(file.s3_uri)
  ) {
    return false
  }
  return true
}

export function is_parseable_backup_file_uri(s3_uri: string): boolean {
  const lower = s3_uri.toLowerCase()
  return lower.endsWith(".pdf") || lower.endsWith(".xlsx") || lower.endsWith(".xls")
}

export function can_parse_backup_file(file: DeductionFile, deduction: DeductionExt): boolean {
  const displayName = file.display_name.toLowerCase()
  const isBackupFile = file.s3_uri === deduction.backup_s3_uri || displayName.includes("backup")
  if (!isBackupFile) {
    return false
  }
  if (file.s3_uri === deduction.check_s3_uri) {
    return false
  }
  if (
    deduction_has_distributor_managed_backup(deduction) &&
    file.s3_uri !== deduction.backup_s3_uri
  ) {
    return false
  }

  return is_parseable_backup_file_uri(file.s3_uri)
}

export function UserFiles({ deduction, onParseComplete }: FilesProps) {
  let [loading, setLoading] = useState(false)
  let [parsingFiles, setParsingFiles] = useState<Set<string>>(new Set())
  let [setting_backup_uri, set_setting_backup_uri] = useState<string | null>(null)
  let [pending_uploads, set_pending_uploads] = useState<DeductionFile[]>([])
  let [deleted_uris, set_deleted_uris] = useState<Set<string>>(new Set())
  let flag = RefreshFlag.use()

  function handleFileDeleted(s3_uri: string) {
    set_deleted_uris((prev: Set<string>) => new Set(prev).add(s3_uri))
    RefreshFlag.set((flag: boolean) => !flag)
  }

  async function handleUpload(uploadedFiles: File[]) {
    setLoading(true)
    const uploaded = await handleFileUpload(deduction.id, uploadedFiles)
    set_pending_uploads((prev: DeductionFile[]) => files_with_pending_uploads(prev, uploaded))
    setLoading(false)
  }

  function isParsingFile(s3_uri: string): boolean {
    return parsingFiles.has(s3_uri)
  }

  async function handle_set_official_backup(
    s3_uri: string,
    confirm_replace_distributor_backup: boolean
  ) {
    set_setting_backup_uri(s3_uri)
    const res = await set_backup_from_user_file(deduction.id, s3_uri, {
      confirm_replace_distributor_backup,
    })
    set_setting_backup_uri(null)
    if (res.ok) {
      toast({
        title: "Official backup set",
        description:
          "This file is now the official backup used for coding, CodingWiz parsing and disputes. Other files stay attached.",
      })
      RefreshFlag.set((f: boolean) => !f)
    } else {
      toast({
        title: "Could not set backup",
        description: res.error.message ?? "Request failed",
        variant: "destructive",
      })
    }
  }

  async function handleParseFile(s3_uri: string, e: Event, mode: ParseFileMode = "auto") {
    e.stopPropagation()

    if (!is_parseable_backup_file_uri(s3_uri)) {
      toast({
        title: "Cannot parse file",
        description: "Only PDF and Excel backup files can be parsed",
        variant: "destructive",
      })
      return
    }

    setParsingFiles((prev: Set<string>) => new Set(prev).add(s3_uri))

    try {
      const result = await parseFile(deduction.id, s3_uri, mode)
      
      if (result.ok) {
        const data = result.value.data
        if (data.success) {
          const isQueued = !!data.message?.toLowerCase().includes("queued")
          toast({
            title: isQueued ? "Parse queued" : "Parse complete",
            description: data.message || `File parsed as ${data.file_kind}`,
          })
          // Refresh the backup data
          RefreshFlag.set((f: boolean) => !f)
          // Call the callback if the file was a backup
          if (!isQueued && data.file_kind === "backup" && onParseComplete) {
            onParseComplete(data.file_kind)
          }
        } else {
          toast({
            title: "Parse failed",
            description: data.message || "Failed to parse file",
            variant: "destructive",
          })
        }
      } else {
        toast({
          title: "Error",
          description: result.error.message || "Failed to parse file",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("Error parsing file:", error)
      toast({
        title: "Error",
        description: "An unexpected error occurred while parsing",
        variant: "destructive",
      })
    } finally {
      setParsingFiles((prev: Set<string>) => {
        const next = new Set(prev)
        next.delete(s3_uri)
        return next
      })
    }
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleUpload,
    multiple: true,
    noClick: true, // Prevents the dropzone from hijacking clicks
    noKeyboard: true, // Prevents keyboard interactions
  })

  // `deduction` itself is a dep: the refetch that RefreshFlag triggers resolves *after* the flag
  // flips, so keying only on the flag left the list rendering the pre-upload deduction.
  let deduction_files = useMemo(() => getFiles(deduction), [deduction, flag])
  let files = visible_deduction_files(deduction_files, pending_uploads, deleted_uris)
  const distributor_label = deduction.source === "kehe" ? "KeHE" : deduction.customer_name ?? "your distributor"
  const replace_backup_requires_confirm = set_as_backup_requires_confirm(deduction)

  return (
    <div
      {...(getRootProps() as HTMLAttributes<HTMLDivElement>)}
      className={classes(
        "relative",
        isDragActive && "after:absolute after:inset-0 after:bg-primary/10 after:rounded-lg after:z-10"
      )}>
      <input {...getInputProps()} />
      {isDragActive && (
        <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
          <div className="bg-white px-4 py-2 rounded-lg shadow-lg border border-primary">
            <p className="text-primary font-medium">Drop files to upload</p>
          </div>
        </div>
      )}
      <div>
        <div class="flex justify-between items-center my-4">
          <div>
            <h3 class="text-lg font-semibold">Files</h3>
            <p class="text-xs text-gray-500">
              Upload as many supporting files as you need. They are added alongside the official backup.
            </p>
          </div>
          <div className="flex gap-2">
            <GoogleDriveSearch
              orgId={deduction.org_id}
              source={deduction.source}
              invoiceNumber={deduction.invoice_number}
            />
            <button
              class="flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md shadow-sm transition-colors cursor-pointer"
              onClick={open}>
              <Upload className="h-4 w-4 mr-2" />
              Upload
            </button>
          </div>
        </div>
        <BolRequestButtons deduction={deduction} logistics_providers={deduction.logistics_providers} guidance_url={deduction.logistics_provider_guidance_url} />
        <div className="flex flex-col gap-4 my-4">
          {loading && (
            <div class="w-full flex justify-center">
              <LoadingSpinner />
            </div>
          )}
          {files.map((file: DeductionFile, i: number) => {
            const isParsing = isParsingFile(file.s3_uri)
            // Large PDFs are skipped by auto-OCR; offer an explicit force-parse via the Azure job.
            const isLargeFile = (deduction.large_backup_uris ?? []).includes(file.s3_uri)
            return (
              <div
                key={i}
                class={classes(
                  "flex gap-2 items-center p-2 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors w-full cursor-pointer overflow-x-hidden",
                  isParsing && "bg-blue-50 border-blue-200"
                )}
                onClick={() => window.open(`/api/_s3?s3_uri=${encodeURIComponent(file.s3_uri)}`, "_blank")}>
                <Download className="h-4 w-4 flex-shrink-0 text-green-500 hover:text-green-600" />
                <div class="grow">
                  <span
                    class={classes("select-text py-1 text-nowrap pr-2 -ml-1 pl-1", file.deduction_id && "cursor-text")}
                    onClick={
                      file.deduction_id
                        ? (e: Event) => {
                            e.stopPropagation()
                          }
                        : undefined
                    }>
                    {file.display_name}
                  </span>
                  <div class="flex justify-between">
                    <span class="text-gray-500 text-sm">{file.actor_name && capitalCase(file.actor_name)}</span>
                    <span class="text-gray-500 text-sm text-nowrap">{file.date && format(file.date, "PP")}</span>
                  </div>
                </div>
                <div class="flex items-center gap-1">
                  {can_set_as_official_backup(file, deduction) &&
                    (replace_backup_requires_confirm ? (
                      <SetBackupDialog
                        file={file}
                        distributor_label={distributor_label}
                        setting_backup_uri={setting_backup_uri}
                        onConfirm={(s3_uri: string) => void handle_set_official_backup(s3_uri, true)}
                      />
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        class="text-xs h-8 px-2 shrink-0 inline-flex items-center gap-1.5"
                        disabled={setting_backup_uri === file.s3_uri}
                        onClick={(e: Event) => {
                          e.stopPropagation()
                          void handle_set_official_backup(file.s3_uri, false)
                        }}>
                        {setting_backup_uri === file.s3_uri ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                            <span>Saving…</span>
                          </>
                        ) : (
                          "Use as official backup"
                        )}
                      </Button>
                    ))}
                  {can_parse_backup_file(file, deduction) && (
                    <button
                      onClick={(e: Event) =>
                        handleParseFile(file.s3_uri, e, isLargeFile ? "azure_job" : "auto")
                      }
                      disabled={isParsing}
                      class={classes(
                        "flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors cursor-pointer",
                        isParsing
                          ? "cursor-not-allowed bg-blue-50 text-blue-500"
                          : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                      )}
                      title={
                        isLargeFile
                          ? "This PDF is too large for automatic parsing. Parse it with OCR (queued)."
                          : "Parse backup file"
                      }>
                      {isParsing ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <FileSearch className="h-3.5 w-3.5" />
                      )}
                      <span>
                        {isParsing ? "Parsing..." : isLargeFile ? "Parse big file" : "Parse file"}
                      </span>
                    </button>
                  )}
                  {can_delete_user_file(file, deduction) && (
                    <DeleteFileDialog
                      className="hidden xl:block"
                      file={file}
                      deductionId={deduction.id}
                      onDelete={() => handleFileDeleted(file.s3_uri)}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export function getFiles(deduction: DeductionExt): DeductionFile[] {
  let files: DeductionFile[] = []
  const seenUris = new Set<string>()
  function addFileIfNew(file: DeductionFile) {
    if (seenUris.has(file.s3_uri)) {
      return
    }
    seenUris.add(file.s3_uri)
    files.push(file)
  }
  function upsertFileDisplayName(file: DeductionFile) {
    const existing = files.find(existingFile => existingFile.s3_uri === file.s3_uri)
    if (existing) {
      existing.display_name = file.display_name
      return
    }
    addFileIfNew(file)
  }
  if (deduction.user_files) {
    for (const user_file of deduction.user_files) {
      addFileIfNew({
        display_name: user_file.display_name,
        date: user_file.date,
        s3_uri: user_file.s3_uri,
        deduction_id: deduction.id,
        actor_name: user_file.actor_name,
      })
    }
    files = files.sort((a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime())
  }

  const customerName = deduction.source === "kehe" ? "KeHE" : deduction.customer_name
  if (deduction.backup_s3_uri) {
    upsertFileDisplayName({
      display_name: `${customerName} Backup`,
      s3_uri: deduction.backup_s3_uri,
    })
  }
  if (deduction.check_s3_uri) {
    addFileIfNew({
      display_name: `${customerName} Check`,
      s3_uri: deduction.check_s3_uri,
    })
  }
  const supplemental = (deduction as DeductionDetailJson).distributor_backup_s3_uris ?? []
  for (const s3_uri of supplemental) {
    if (s3_uri === deduction.backup_s3_uri) {
      continue
    }
    addFileIfNew({
      display_name: `${customerName} Backup (from portal)`,
      s3_uri,
    })
  }
  return files
}

/** Matches server `db::s3::is_unfi_portal_automation_capture_uri` (Playwright UNFI sync uploads). */
export function s3_uri_is_unfi_portal_automation_capture(uri: string): boolean {
  if (!uri.startsWith("s3://")) {
    return false
  }
  const rest = uri.slice("s3://".length)
  const firstSlash = rest.indexOf("/")
  if (firstSlash === -1) {
    return false
  }
  const key = rest.slice(firstSlash + 1)
  return key.includes("/unfi/")
}

/** Files for disputes, CodingWiz-adjacent UI, etc. Excludes UNFI Playwright portal captures. */
export function deductionFilesExcludingUnfiPortalCaptures(deduction: DeductionExt): DeductionFile[] {
  return getFiles(deduction).filter(f => !s3_uri_is_unfi_portal_automation_capture(f.s3_uri))
}

export function getDisputeAttachmentFiles(deduction: DeductionExt): DeductionFile[] {
  return deductionFilesExcludingUnfiPortalCaptures(deduction)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function test_deduction(
    overrides: Partial<DeductionExt> &
      Partial<Pick<DeductionDetailJson, "distributor_backup_s3_uris">> = {}
  ): DeductionExt {
    return {
      id: "deduction-1",
      source: "kehe",
      customer_name: "Albertson LLC",
      backup_s3_uri: "s3://bucket/backup.xlsx",
      check_s3_uri: null,
      user_files: [],
      ...overrides,
    } as DeductionExt
  }

  describe("distributor managed backup helpers", () => {
    it("detects kehe portal paths as distributor-managed", () => {
      expect(
        s3_uri_is_distributor_managed_backup(
          "s3://bucket/org/uuid/kehe_deductions/ded-id/Backup.pdf"
        )
      ).toBe(true)
      expect(
        s3_uri_is_user_uploaded_file("s3://bucket/org/uuid/user_files/deduction/ded-id/a.pdf")
      ).toBe(true)
      expect(
        s3_uri_is_distributor_managed_backup(
          "s3://bucket/org/uuid/user_files/deduction/ded-id/a.pdf"
        )
      ).toBe(false)
    })

    it("set_as_backup_requires_confirm when canonical backup is distributor-managed", () => {
      const ded = test_deduction({
        backup_s3_uri: "s3://bucket/org/uuid/kehe_deductions/deduction-1/Backup.pdf",
      })
      expect(set_as_backup_requires_confirm(ded)).toBe(true)
      expect(
        can_set_as_official_backup(
          {
            display_name: "upload.pdf",
            s3_uri: "s3://bucket/org/uuid/user_files/deduction/deduction-1/upload.pdf",
            deduction_id: "deduction-1",
          },
          ded
        )
      ).toBe(true)
    })

    it("can_parse_backup_file blocks user uploads when distributor backup is canonical", () => {
      const ded = test_deduction({
        backup_s3_uri: "s3://bucket/org/uuid/kehe_deductions/deduction-1/Backup.pdf",
      })
      expect(
        can_parse_backup_file(
          { display_name: "my-backup.pdf", s3_uri: ded.backup_s3_uri!, deduction_id: "deduction-1" },
          ded
        )
      ).toBe(true)
      expect(
        can_parse_backup_file(
          {
            display_name: "other-backup.pdf",
            s3_uri: "s3://bucket/org/uuid/user_files/deduction/deduction-1/other.pdf",
            deduction_id: "deduction-1",
          },
          ded
        )
      ).toBe(false)
    })
  })

  describe("s3_uri_is_unfi_portal_automation_capture", () => {
    it("detects org/.../unfi/... keys", () => {
      expect(
        s3_uri_is_unfi_portal_automation_capture("s3://b/org/uuid/unfi/check/EAST-1.pdf"),
      ).toBe(true)
    })
    it("excludes user_files and unfi_disputes paths", () => {
      expect(
        s3_uri_is_unfi_portal_automation_capture("s3://b/org/uuid/user_files/deduction/x/a.pdf"),
      ).toBe(false)
      expect(
        s3_uri_is_unfi_portal_automation_capture("s3://b/org/uuid/unfi_disputes/j/form.xlsx"),
      ).toBe(false)
    })
  })

  describe("can_parse_backup_file", () => {
    it("allows Excel backup files", () => {
      expect(
        can_parse_backup_file(
          { display_name: "KeHE Backup.xlsx", s3_uri: "s3://bucket/backup.xlsx", deduction_id: "deduction-1" },
          test_deduction()
        )
      ).toBe(true)
    })

    it("allows PDF backup files", () => {
      expect(
        can_parse_backup_file(
          { display_name: "KeHE Backup.pdf", s3_uri: "s3://bucket/backup.pdf", deduction_id: "deduction-1" },
          test_deduction({ backup_s3_uri: "s3://bucket/backup.pdf" })
        )
      ).toBe(true)
    })

    it("rejects non-backup spreadsheets", () => {
      expect(
        can_parse_backup_file(
          { display_name: "Check.xlsx", s3_uri: "s3://bucket/check.xlsx", deduction_id: "deduction-1" },
          test_deduction()
        )
      ).toBe(false)
    })
  })

  describe("files_with_pending_uploads", () => {
    const server_file: DeductionFile = {
      display_name: "KeHE Backup",
      s3_uri: "s3://bucket/backup.xlsx",
      deduction_id: "deduction-1",
    }
    const uploaded: DeductionFile = {
      display_name: "proof-of-delivery.pdf",
      s3_uri: "s3://bucket/org/uuid/user_files/deduction/deduction-1/pod.pdf",
      deduction_id: "deduction-1",
    }

    it("shows a just-uploaded file before the deduction refetch lands", () => {
      const files = files_with_pending_uploads([server_file], [uploaded])
      expect(files.map((f: DeductionFile) => f.s3_uri)).toEqual([uploaded.s3_uri, server_file.s3_uri])
    })

    it("collapses the pending entry once the server list carries it", () => {
      const files = files_with_pending_uploads([uploaded, server_file], [uploaded])
      expect(files).toHaveLength(2)
      expect(files.filter((f: DeductionFile) => f.s3_uri === uploaded.s3_uri)).toHaveLength(1)
    })

    it("returns the server list untouched when nothing is pending", () => {
      expect(files_with_pending_uploads([server_file], [])).toEqual([server_file])
    })
  })

  describe("visible_deduction_files", () => {
    const server_file: DeductionFile = {
      display_name: "KeHE Backup",
      s3_uri: "s3://bucket/backup.xlsx",
      deduction_id: "deduction-1",
    }
    const uploaded: DeductionFile = {
      display_name: "proof-of-delivery.pdf",
      s3_uri: "s3://bucket/org/uuid/user_files/deduction/deduction-1/pod.pdf",
      deduction_id: "deduction-1",
    }

    it("drops a deleted file before the deduction refetch lands", () => {
      const files = visible_deduction_files([server_file, uploaded], [], new Set([uploaded.s3_uri]))
      expect(files).toEqual([server_file])
    })

    it("does not let a pending upload resurrect a file deleted right after uploading it", () => {
      const files = visible_deduction_files([server_file], [uploaded], new Set([uploaded.s3_uri]))
      expect(files).toEqual([server_file])
    })

    it("is the plain merged list when nothing was deleted", () => {
      expect(visible_deduction_files([server_file], [uploaded], new Set())).toEqual([uploaded, server_file])
    })
  })

  describe("getFiles", () => {
    it("shows canonical KeHE backups with distributor title even when uploaded file has raw filename", () => {
      const files = getFiles(
        test_deduction({
          user_files: [
            {
              display_name: "eecfd236-fec9-48a5-ba0f-e8152112a7e2.xlsx",
              s3_uri: "s3://bucket/backup.xlsx",
              date: "2026-05-25T12:00:00Z",
              actor_name: "joey",
            },
          ],
        })
      )

      expect(files).toHaveLength(1)
      expect(files[0].display_name).toBe("KeHE Backup")
      expect(files[0].date).toBe("2026-05-25T12:00:00Z")
    })

    it("shows supplemental portal backups alongside user-uploaded official backup", () => {
      const kehe_portal_uri = "s3://bucket/org/uuid/kehe_deductions/deduction-1/Backup.pdf"
      const user_backup_uri = "s3://bucket/org/uuid/user_files/deduction/deduction-1/upload.pdf"
      const files = getFiles(
        test_deduction({
          backup_s3_uri: user_backup_uri,
          distributor_backup_s3_uris: [kehe_portal_uri],
        })
      )

      expect(files).toHaveLength(2)
      expect(files.some(f => f.s3_uri === user_backup_uri && f.display_name === "KeHE Backup")).toBe(true)
      expect(
        files.some(f => f.s3_uri === kehe_portal_uri && f.display_name === "KeHE Backup (from portal)")
      ).toBe(true)
    })
  })
}
